import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { 
  Code, 
  Smartphone, 
  Brain, 
  Cloud, 
  Shield, 
  Database,
  Zap,
  Globe,
  Settings,
  BarChart3,
  Palette,
  Headphones,
  Layers
} from 'lucide-react';

const ServicesSection = styled.section`
  background: linear-gradient(135deg, #0a0e27 0%, #1e1b4b 40%, #581c87 100%);
  padding: 100px 0;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-image: radial-gradient(circle at 1px 1px, rgba(255,255,255,0.3) 0.5px, transparent 0);
    background-size: 30px 30px;
    opacity: 0.5;
  }
`;

const ServicesContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  position: relative;
  z-index: 2;
`;

const SectionTitle = styled.h2`
  font-size: 3rem;
  text-align: center;
  margin-bottom: 20px;
  background: linear-gradient(45deg, #00ff41, #00ffff, #ff0080, #ffff00);
  background-size: 300% 300%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 700;
  letter-spacing: -0.02em;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, #00ff41, #00ffff, #ff0080);
    border-radius: 2px;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  font-size: 1.3rem;
  color: #ffffff;
  margin-bottom: 80px;
  max-width: 700px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 60px;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 40px;
  margin-bottom: 80px;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }
`;

const ServiceCard = styled(motion.div)`
  background: rgba(135,206,235,0.1);
  backdrop-filter: blur(15px);
  border: 1px solid rgba(135,206,235,0.3);
  padding: 50px 40px;
  border-radius: 25px;
  text-align: center;
  transition: all 0.4s ease;
  position: relative;
  overflow: hidden;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: -100%;
    width: 100%;
    height: 100%;
    background: linear-gradient(90deg, transparent, rgba(135,206,235,0.1), transparent);
    transition: left 0.6s;
  }
  
  &:hover {
    transform: translateY(-10px);
    border-color: rgba(135,206,235,0.6);
    box-shadow: 0 20px 40px rgba(0,0,0,0.3);
    
    &::before {
      left: 100%;
    }
  }
`;

const ServiceIcon = styled.div`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 30px;
  color: white;
  box-shadow: 0 10px 30px rgba(135,206,235,0.3);
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  color: #87ceeb;
  margin-bottom: 20px;
  font-weight: 700;
`;

const ServiceDescription = styled.p`
  color: #ffffff;
  line-height: 1.7;
  font-size: 1rem;
  margin-bottom: 25px;
`;

const ServiceFeatures = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  text-align: left;
  flex: 1;
`;

const ServiceFeature = styled.li`
  color: rgba(255,255,255,0.9);
  margin-bottom: 10px;
  padding-left: 20px;
  position: relative;
  font-size: 0.95rem;
  
  &::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #00ff41;
    font-weight: bold;
  }
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #87ceeb 0%, #1e3a8a 100%);
  color: white;
  border: none;
  padding: 15px 30px;
  border-radius: 25px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: auto;
  align-self: center;
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 25px rgba(135,206,235,0.4);
  }
`;

const ProfessionalServices = () => {
  const navigate = useNavigate();

  const handleQuoteClick = (serviceName) => {
    navigate(`/quote?service=${encodeURIComponent(serviceName)}`);
  };
  const services = [
    {
      icon: <Code size={50} />,
      title: 'Custom Software Development',
      description: 'End-to-end software solutions tailored to your business needs with modern technologies and best practices.',
      features: [
        'Full-stack web applications',
        'Enterprise software solutions',
        'API development & integration',
        'Legacy system modernization',
        'Scalable architecture design'
      ]
    },
    {
      icon: <Smartphone size={50} />,
      title: 'Mobile App Development',
      description: 'Native and cross-platform mobile applications that deliver exceptional user experiences across all devices.',
      features: [
        'iOS & Android native apps',
        'React Native development',
        'Flutter cross-platform apps',
        'App Store optimization',
        'Mobile UI/UX design'
      ]
    },
    {
      icon: <Brain size={50} />,
      title: 'AI & Machine Learning',
      description: 'Intelligent solutions powered by artificial intelligence to automate processes and drive data-driven decisions.',
      features: [
        'Machine learning models',
        'Natural language processing',
        'Computer vision solutions',
        'Predictive analytics',
        'AI-powered automation'
      ]
    },
    {
      icon: <Cloud size={50} />,
      title: 'Cloud Solutions',
      description: 'Comprehensive cloud services including migration, architecture, and management for scalable infrastructure.',
      features: [
        'AWS, Azure, GCP services',
        'Cloud migration strategies',
        'Serverless architecture',
        'DevOps & CI/CD pipelines',
        'Cloud security implementation'
      ]
    },
    {
      icon: <Layers size={50} />,
      title: 'Architectural & Civil Design',
      description: 'Professional architectural and civil design services delivering innovative, functional, and aesthetically compelling structures for residential, commercial, and infrastructure projects.',
      features: [
        'Residential & commercial architecture',
        'Structural & civil engineering design',
        '2D/3D blueprints & CAD drawings',
        'Interior space planning',
        'Site planning & landscape design'
      ]
    },
    {
      icon: <Shield size={50} />,
      title: 'Cybersecurity Services',
      description: 'Comprehensive security solutions to protect your digital assets and ensure compliance with industry standards.',
      features: [
        'Security audits & assessments',
        'Penetration testing',
        'GDPR compliance solutions',
        'Data encryption & protection',
        'Security monitoring systems'
      ]
    },
    {
      icon: <Database size={50} />,
      title: 'Data Analytics & BI',
      description: 'Transform your data into actionable insights with advanced analytics and business intelligence solutions.',
      features: [
        'Data warehouse design',
        'Business intelligence dashboards',
        'Real-time analytics',
        'Data visualization',
        'Big data processing'
      ]
    },
    {
      icon: <Palette size={50} />,
      title: 'UI/UX Design',
      description: 'User-centered design solutions that create intuitive and engaging digital experiences for your customers.',
      features: [
        'User experience research',
        'Interface design systems',
        'Prototyping & wireframing',
        'Usability testing',
        'Brand identity design'
      ]
    },
    {
      icon: <Settings size={50} />,
      title: 'Digital Transformation',
      description: 'Complete digital transformation services to modernize your business processes and technology infrastructure.',
      features: [
        'Process automation',
        'System integration',
        'Technology consulting',
        'Change management',
        'Digital strategy planning'
      ]
    },
    {
      icon: <Headphones size={50} />,
      title: '24/7 Support & Maintenance',
      description: 'Ongoing support and maintenance services to ensure your systems run smoothly and stay up-to-date.',
      features: [
        'Round-the-clock monitoring',
        'Bug fixes & updates',
        'Performance optimization',
        'Technical support',
        'System maintenance'
      ]
    }
  ];

  return (
    <ServicesSection>
      <ServicesContainer>
        <SectionTitle>Our Professional Services</SectionTitle>
        <SectionSubtitle>
          Comprehensive technology solutions designed to accelerate your business growth and digital transformation journey
        </SectionSubtitle>
        
        <ServicesGrid>
          {services.map((service, index) => (
            <ServiceCard
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              onClick={() => handleQuoteClick(service.title)}
            >
              <ServiceIcon>
                {service.icon}
              </ServiceIcon>
              <ServiceTitle>{service.title}</ServiceTitle>
              <ServiceDescription>{service.description}</ServiceDescription>
              <ServiceFeatures>
                {service.features.map((feature, idx) => (
                  <ServiceFeature key={idx}>{feature}</ServiceFeature>
                ))}
              </ServiceFeatures>
              <CTAButton onClick={() => handleQuoteClick(service.title)}>Get Quote</CTAButton>
            </ServiceCard>
          ))}
        </ServicesGrid>
      </ServicesContainer>
    </ServicesSection>
  );
};

export default ProfessionalServices;